/**
 * 图层复合命令（M4-05 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 复合只快照"可见性/不透明度/位置(transform)/样式启用状态"四类开关——
 * 不快照结构（哪些图层存在）也不快照像素，所以创建复合之后新增/删除图层
 * 仍然对所有既有复合生效（验收标准 4）：应用复合时只处理"当下还存在、
 * 且复合里也记录过"的图层交集，其余图层保持原状。
 */
import type { Command, Json } from '../command-bus';
import { walkLayers } from '../../document/draft';
import type { DocumentDraft } from '../../document/draft';
import type { Document, LayerComp, LayerCompEntry } from '../../document/types';

const STRUCTURAL_COST = 128;

/** 快照当前文档所有图层的复合相关状态。 */
export function snapshotLayerComp(document: Document, id: string, name: string): LayerComp {
  const entries: LayerCompEntry[] = [];
  for (const { layer } of walkLayers(document.root)) {
    entries.push({
      layerId: layer.id,
      visible: layer.visible,
      opacity: layer.opacity,
      transform: layer.transform,
      effectsEnabled: layer.effects.map((e) => e.enabled),
    });
  }
  return { id, name, entries };
}

/** 把复合的每一条状态应用到草稿——图层不存在时跳过这条（不是失败）。 */
function applyEntries(draft: DocumentDraft, entries: readonly LayerCompEntry[]): void {
  for (const entry of entries) {
    draft.updateLayer(entry.layerId, (layer) => {
      const effectsEnabled = entry.effectsEnabled;
      const effects =
        effectsEnabled.length === layer.effects.length
          ? layer.effects.map((effect, i) => ({ ...effect, enabled: effectsEnabled[i]! }))
          : layer.effects; // 样式列表变了（数量对不上）：不猜测映射关系，样式保持原状
      return {
        ...layer,
        visible: entry.visible,
        opacity: entry.opacity,
        transform: entry.transform,
        effects,
      };
    });
  }
}

/** 新建复合：把当前状态存进列表，可逆（逆操作是把列表存回去）。 */
export function createAddLayerCompCommand(comp: LayerComp): Command {
  return {
    kind: 'layerComp.add',
    label: `新建图层复合「${comp.name}」`,
    cost: STRUCTURAL_COST * (comp.entries.length + 1),
    // `LayerComp` 没有索引签名，但字段全是纯数据；转换是精确类型，不是 any
    record: { kind: 'layerComp.add', args: { comp } as unknown as Readonly<Record<string, Json>> },
    apply(draft) {
      draft.setLayerComps([...draft.current.layerComps, comp]);
    },
    invert() {
      return createRemoveLayerCompCommand(comp.id);
    },
  };
}

export function createRemoveLayerCompCommand(id: string): Command {
  return {
    kind: 'layerComp.remove',
    label: '删除图层复合',
    cost: STRUCTURAL_COST,
    record: { kind: 'layerComp.remove', args: { id } },
    apply(draft) {
      draft.setLayerComps(draft.current.layerComps.filter((c) => c.id !== id));
    },
    invert(document) {
      const comp = document.layerComps.find((c) => c.id === id);
      return comp === undefined ? null : createAddLayerCompCommand(comp);
    },
  };
}

/**
 * 应用复合：把 id 对应复合里的每条状态套到当前文档。
 * 逆操作不是"应用另一个复合"，而是直接内联当前（应用前）的状态——
 * 这样撤销永远精确，不依赖列表里是否存在一个"应用前"的复合。
 */
export function createApplyLayerCompCommand(id: string): Command {
  return {
    kind: 'layerComp.apply',
    label: '应用图层复合',
    cost: STRUCTURAL_COST * 8, // 实际条数取决于复合大小，此处给个保守估计
    record: { kind: 'layerComp.apply', args: { id } },
    apply(draft) {
      const comp = draft.current.layerComps.find((c) => c.id === id);
      if (comp === undefined) return;
      applyEntries(draft, comp.entries);
    },
    invert(document) {
      const comp = document.layerComps.find((c) => c.id === id);
      if (comp === undefined) return null;
      // 只需要回滚这个复合会碰到的那些图层——用它的 layerId 集合从当前文档取值
      const before: LayerCompEntry[] = [];
      for (const entry of comp.entries) {
        const current = findLayerEntry(document, entry.layerId);
        if (current !== null) before.push(current);
      }
      return createApplyRawEntriesCommand(before);
    },
  };
}

function findLayerEntry(document: Document, layerId: LayerCompEntry['layerId']): LayerCompEntry | null {
  for (const { layer } of walkLayers(document.root)) {
    if (layer.id === layerId) {
      return {
        layerId: layer.id,
        visible: layer.visible,
        opacity: layer.opacity,
        transform: layer.transform,
        effectsEnabled: layer.effects.map((e) => e.enabled),
      };
    }
  }
  return null;
}

/** 内部命令：直接套用一组内联的图层状态，不查列表。只用作 `apply` 的 invert。 */
function createApplyRawEntriesCommand(entries: readonly LayerCompEntry[]): Command {
  return {
    kind: 'layerComp.applyRaw',
    label: '还原图层状态',
    cost: STRUCTURAL_COST * (entries.length + 1),
    apply(draft) {
      applyEntries(draft, entries);
    },
    invert(document) {
      const before: LayerCompEntry[] = [];
      for (const entry of entries) {
        const current = findLayerEntry(document, entry.layerId);
        if (current !== null) before.push(current);
      }
      return createApplyRawEntriesCommand(before);
    },
  };
}
